import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { Reservations, Result, RootState, Subtask } from 'constants/interfaces'
import keys from 'constants/keys'
import { updateResultStatus } from 'redux/actions/tasks_v2'
import { extractSubstringAfterDelimiter } from 'utils'
import { useTaskOverviewContext } from '../TaskOverviewStackProvider'
import {
  canReviewMediaResult,
  getReviewState,
  hasReachedReworkLimit,
  IReviewFeedback,
  IRatingFeedback,
} from '../model/review'
import { ReservationMode } from '../Content/MediaSubtasksCompleting/ReservationsModal'

type FeedbackOutcome =
  | { type: 'review'; kind: IReviewFeedback['kind'] }
  | { type: 'rating'; rating: IRatingFeedback['rating'] }

export const useMediaReview = (subtask: Subtask, result: Result | undefined, isReadMode: boolean) => {
  const { task, resultContextId, setIsSubtaskBusy } = useTaskOverviewContext()
  const dispatch: AppDispatch = useDispatch()
  const uid = useSelector((state: RootState) => state.auth.uid)
  const rid = useSelector((state: RootState) => state.config.config.rid)
  const [rating, setRating] = useState(result?.rating ?? 0)
  const [isReservationsModalOpen, setIsReservationsModalOpen] = useState(false)
  const [reservationsMode, setReservationsMode] = useState<ReservationMode>('reject')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const [feedback, setFeedback] = useState<(FeedbackOutcome & { id: number; scope: string }) | null>(null)
  const lock = useRef(false)
  const mounted = useRef(true)
  const feedbackId = useRef(0)
  const scope = JSON.stringify([subtask.tid, subtask.st_id, resultContextId, task.recurrence_ts ?? null])
  const currentScope = useRef(scope)
  currentScope.current = scope
  const review = getReviewState(result)
  const currentReworkCount = result?.rework_count ?? 0
  const reworkLimit = task.rework_limit
  const isReworkLimitReached = hasReachedReworkLimit(reworkLimit, currentReworkCount)
  const canReview = canReviewMediaResult(task, uid, isReadMode, result)

  const showFeedback = (outcome: FeedbackOutcome, requestScope: string) => {
    if (!mounted.current || currentScope.current !== requestScope) return
    setFeedback({ id: ++feedbackId.current, ...outcome, scope: requestScope })
  }

  const save = async (status: number, nextRating: number, reservations?: Reservations, incrementRework = false) => {
    if (!canReview || lock.current) return false
    lock.current = true
    setBusy(true)
    setError(false)
    setFeedback(null)
    try {
      const ok = await dispatch(
        updateResultStatus({
          payload: {
            tid: subtask.tid,
            st_id: subtask.st_id,
            task_context_id: extractSubstringAfterDelimiter(task.retail_id_context_id, rid),
            result_context_id: resultContextId,
            status,
            rating:
              nextRating >= keys.RATING_VALUES.POOR && nextRating <= keys.RATING_VALUES.EXCELLENT
                ? nextRating
                : undefined,
            recurrence_ts: task.recurrence_ts ?? null,
            ...(incrementRework ? { rework_count: currentReworkCount + 1 } : {}),
            ...(reservations ? { reservations } : {}),
          },
          type: subtask.type,
          optimistic: false,
        })
      )
      if (!ok) throw new Error('Review was not saved')
      return mounted.current && currentScope.current === scope
    } catch {
      if (mounted.current && currentScope.current === scope) setError(true)
      return false
    } finally {
      lock.current = false
      if (mounted.current) setBusy(false)
    }
  }

  const handleApprove = async () => {
    if (review.isApproved) return false

    const ok = await save(keys.TASK_STATUSES.APPROVED.value, rating, { reasons: [], score_deduction: 0 })
    if (ok) showFeedback({ type: 'review', kind: 'approved' }, scope)
    return ok
  }

  const handleReject = () => {
    if (!canReview || lock.current || (isReworkLimitReached && !review.isRejected)) return
    if (review.isRejected) {
      save(keys.TASK_STATUSES.DONE.value, 0, { reasons: [], score_deduction: 0 })
    } else {
      setReservationsMode('reject')
      setIsReservationsModalOpen(true)
    }
  }

  const handleApproveWithReservation = () => {
    if (!canReview || lock.current) return
    setReservationsMode('approve_with_reservation')
    setIsReservationsModalOpen(true)
  }

  const handleReservationsConfirm = async (reservations: Reservations, nextRating: number) => {
    const rejecting = reservationsMode === 'reject'
    const ok = await save(
      rejecting ? keys.TASK_STATUSES.DISAPPROVED.value : keys.TASK_STATUSES.APPROVED_WITH_RESERVATION.value,
      rejecting ? 0 : nextRating,
      reservations,
      rejecting
    )
    if (ok && mounted.current && currentScope.current === scope) {
      setIsReservationsModalOpen(false)
      showFeedback({ type: 'review', kind: rejecting ? 'rejected' : 'approved' }, scope)
    }
  }

  const handleRating = async (value: number) => {
    if (!canReview || lock.current) return
    setRating(value)
    if (review.isApproved) {
      const ok = await save(result!.status, value, result?.reservations ?? { reasons: [], score_deduction: 0 })
      if (ok) showFeedback({ type: 'rating', rating: value }, scope)
    }
  }

  const closeReservations = () => {
    if (!lock.current) setIsReservationsModalOpen(false)
  }

  useEffect(() => {
    setRating(result?.rating ?? 0)
  }, [result])

  useEffect(() => {
    setFeedback(null)
  }, [scope])

  useEffect(() => {
    if (!feedback) return
    const timeout = window.setTimeout(() => setFeedback(null), 1000)
    return () => window.clearTimeout(timeout)
  }, [feedback])

  useEffect(() => {
    setIsSubtaskBusy(busy)
    return () => setIsSubtaskBusy(false)
  }, [busy, setIsSubtaskBusy])

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  return {
    ...review,
    rating,
    reservations: result?.reservations,
    isReservationsModalOpen,
    reservationsMode,
    busy,
    error,
    reviewFeedback: feedback?.scope === scope && feedback.type === 'review' ? feedback : null,
    ratingFeedback: feedback?.scope === scope && feedback.type === 'rating' ? feedback : null,
    canReview,
    currentReworkCount,
    reworkLimit,
    isReworkLimitReached,
    handleApprove,
    handleReject,
    handleApproveWithReservation,
    handleReservationsConfirm,
    handleRating,
    closeReservations,
  }
}
